/**
 * PWA registration + install-prompt helper.
 *
 * - Registers /sw.js silently. Failures are non-fatal — the SPA still
 *   works, just without offline cache.
 * - Captures the `beforeinstallprompt` event so we can surface our own
 *   "Add to Home Screen" UI later (Android Chrome). iOS Safari has
 *   no such event — users must use Share → Add to Home Screen
 *   manually; we show a hint via UA detection in the Layout.
 */

let deferredPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function registerSW(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // Don't block startup — register after the page settles.
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((err) => {
        // Surface to console only — analytics isn't critical here.
        console.warn('[pwa] SW registration failed', err);
      });
  });

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome;
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** True if the app is running as a PWA (added to home screen, standalone display). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS-specific
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Best-effort iOS Safari detection — used to show the manual A2HS hint. */
export function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}
